"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import { useSearchParams } from "next/navigation";
import { ArrowLeft } from "lucide-react";
import { HireAgentForm } from "~/components/marketplace/hire-agent-form";
import { LandingAgentSidebar, type SidebarMode } from "~/app/components/landing-agent-sidebar";
import { ContactSalesModal } from "~/components/marketing/contact-sales-modal";
import { api } from "~/trpc/react";
import type { HirePlanSelection, MarketplaceHirePlanId } from "~/lib/marketplace/hire-pricing";
import {
  computeHireTotalCents,
  defaultHirePlanSelection,
  formatHireTotalPrice,
  readHirePlanSelection,
  writeHirePlanSelection,
} from "~/lib/marketplace/hire-pricing";
import {
  computeRecommendationsTotalCents,
  defaultRecommendationsFromSuggestion,
  INCLUDED_FREE_CHAT_CONVERSATIONS,
  normalizeRecommendationsSelection,
  readHireRecommendationsSelection,
  resetRecommendationsToFreeTier,
  writeHireRecommendationsSelection,
  type HireRecommendationsSelection,
} from "~/lib/marketplace/hire-recommendations-pricing";
import { readWebsiteSuggestion } from "~/lib/marketplace/hire-website-suggestion";
import { readHireCustomPreviewState } from "~/lib/marketplace/marketplace-hire-flow";
import { analyzeWebsiteForHire } from "~/lib/marketplace/analyze-website-for-hire";
import {
  hireWebsiteSessionMatches,
  resetHireBuilderForNewAgent,
} from "~/lib/marketplace/hire-onboarding-session";
import {
  rankSuggestedAgents,
  type WebsiteAgentSuggestion,
} from "~/lib/marketplace/website-suggestions";
import type { MoonlightAgentListItem } from "~/lib/marketplace/moonlight-home";
import type { HireTemplateDefaults } from "~/lib/marketplace/hire-template-defaults";
import {
  AGENT_PROFILE_SIDEBAR_DEFAULT_WIDTH_PX,
  type AgentSidebarOpenDetail,
} from "~/lib/marketplace/site-agent-sidebar";
import { cn } from "~/lib/utils";
import {
  SITEGLUE_CTA_CONFIG,
  trackSiteGlueCta,
} from "~/lib/siteglue/cta-config";
import { applyOnboardingGoalsToSelection, readSavedHireBuilderOnboardingState } from "~/lib/marketplace/hire-builder-onboarding";
import { resolveHireBusinessDisplayName } from "~/lib/marketplace/hire-company-name";
import { buildMarketplaceHireCompletionPath } from "~/lib/marketplace/marketplace-hire-flow";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import MarketingNav from "~/app/components/marketing-nav";
import { useBrand } from "~/config/brands";
import { persistMarketingBrand, resolveClientMarketingBrand } from "~/lib/marketing-brand";
import {
  aiEmployeePlanIdFromHirePlanId,
  aiEmployeePlanPriceCents,
  formatAiEmployeePlanHeaderLabel,
  type AiEmployeePlanId,
} from "~/lib/siteglue/ai-employee-plans";
import type { PartnerOnboardingAgentConfig } from "~/lib/org-onboarding/types";

type MarketplaceHireBuilderShellProps = {
  templateProjectId: string;
  agentName: string;
  defaultPlanId?: MarketplaceHirePlanId;
  backHref: string;
  showPlanStep?: boolean;
  pageTitle?: string;
  backLabel?: string;
  templateDefaults?: HireTemplateDefaults;
  partnerConfig?: PartnerOnboardingAgentConfig;
};

function defaultCustomHireSelection(): HireRecommendationsSelection {
  return resetRecommendationsToFreeTier(
    normalizeRecommendationsSelection({
      liveChat: true,
      chatConversations: INCLUDED_FREE_CHAT_CONVERSATIONS,
      locationCount: 1,
      departmentCount: 1,
      teamMemberCount: 1,
      teamHandoffEnabled: true,
    }),
  );
}

function selectionFromWebsiteSuggestion(
  suggestion: WebsiteAgentSuggestion,
): HireRecommendationsSelection {
  return resetRecommendationsToFreeTier(defaultRecommendationsFromSuggestion(suggestion));
}

function preferredDemoTab(
  agent: MoonlightAgentListItem,
): NonNullable<AgentSidebarOpenDetail["tab"]> {
  if (agent.channels.aiChat) return "aiChat";
  if (agent.channels.contact) return "contact";
  if (agent.channels.talk) return "talk";
  if (agent.videoCallEnabled) return "videoCall";
  if (agent.channels.call) return "call";
  return "aiChat";
}

export function MarketplaceHireBuilderShell({
  templateProjectId,
  agentName,
  defaultPlanId = "intern",
  backHref,
  showPlanStep = false,
  pageTitle,
  backLabel = "Back",
  templateDefaults,
  partnerConfig,
}: MarketplaceHireBuilderShellProps) {
  const [websiteSuggestion, setWebsiteSuggestion] = useState<WebsiteAgentSuggestion | null>(
    null,
  );
  const [displayCompanyName, setDisplayCompanyName] = useState("");
  const [liveAgentName, setLiveAgentName] = useState("");
  const [recommendationsSelection, setRecommendationsSelection] =
    useState<HireRecommendationsSelection | null>(() => {
      if (!showPlanStep) return null;
      // Prefer persisted selection (survives Google OAuth full-page return).
      return readHireRecommendationsSelection() ?? defaultCustomHireSelection();
    });
  const [demoAgentId, setDemoAgentId] = useState<string | null>(null);
  const [demoTab, setDemoTab] = useState<NonNullable<AgentSidebarOpenDetail["tab"]>>("aiChat");
  const [demoSidebarMode, setDemoSidebarMode] = useState<SidebarMode>("closed");
  const [demoSidebarWidthPx, setDemoSidebarWidthPx] = useState(
    AGENT_PROFILE_SIDEBAR_DEFAULT_WIDTH_PX,
  );
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const [pendingCloneAgent, setPendingCloneAgent] = useState<MoonlightAgentListItem | null>(
    null,
  );
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const bootstrapStartedRef = useRef(false);
  /** Prevents ?new=1 session clear from re-running until the param is stripped. */
  const newBuildHandledRef = useRef(false);
  /** Prevents layout UI reset / key bump from re-running while ?new=1 is still present. */
  const newBuildUiResetRef = useRef(false);
  /**
   * Clear hire session synchronously during render (before children hydrate) so
   * Company Profile / training draft cannot flash stale content on + Build new agent.
   */
  if (typeof window !== "undefined" && !newBuildHandledRef.current) {
    const isNewBuild = searchParams.get("new") === "1";
    const hireIntentId = searchParams.get("hireIntentId")?.trim();
    if (isNewBuild && !hireIntentId) {
      newBuildHandledRef.current = true;
      resetHireBuilderForNewAgent();
    }
  }
  const utils = api.useUtils();
  const brand = useBrand();
  const isSiteGlueBrand =
    !partnerConfig &&
    (brand.id === "siteglue" ||
      resolveClientMarketingBrand(searchParams) === "siteglue");
  /** After Sign Up / email confirm: hide marketing nav + dashboard chrome during onboarding. */
  const hideSiteGlueChrome = Boolean(isSiteGlueBrand && authLoaded && isSignedIn);
  const productLabel = partnerConfig
    ? partnerConfig.displayName
    : isSiteGlueBrand
      ? "SiteGlue"
      : "Inbound GPT";

  useEffect(() => {
    const next = isSiteGlueBrand ? "siteglue" : "inbound_gpt";
    persistMarketingBrand(next);
    // Title only — never mutate <link rel="icon"> (removeChild races crash the app shell).
    if (typeof document !== "undefined") {
      document.title = isSiteGlueBrand
        ? "Onboard Your AI Employee | SiteGlue"
        : "Inbound GPT";
      if (isSiteGlueBrand) {
        document.documentElement.setAttribute("data-marketing-brand", "siteglue");
      }
    }
  }, [isSiteGlueBrand]);

  const [buildSessionKey, setBuildSessionKey] = useState(0);

  const [bootstrapComplete, setBootstrapComplete] = useState(!showPlanStep);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [, setNeedsWebsiteInput] = useState(false);

  const [planSelection, setPlanSelection] = useState<HirePlanSelection>(() => {
    const stored = readHirePlanSelection();
    return stored ?? defaultHirePlanSelection(defaultPlanId);
  });

  const runWebsiteBootstrap = useCallback(async (rawWebsite: string) => {
    setBootstrapError(null);
    setIsBootstrapping(true);
    bootstrapStartedRef.current = true;

    try {
      const analyzed = await analyzeWebsiteForHire(rawWebsite, {
        skipOnboardingAlert: isSiteGlueBrand,
      });
      setWebsiteSuggestion(analyzed);
      const selection = selectionFromWebsiteSuggestion(analyzed);
      setRecommendationsSelection(selection);
      writeHireRecommendationsSelection(selection);
      setBootstrapComplete(true);
    } catch (error) {
      bootstrapStartedRef.current = false;
      setBootstrapError(
        error instanceof Error ? error.message : "Could not analyze that website.",
      );
    } finally {
      setIsBootstrapping(false);
    }
  }, [isSiteGlueBrand]);

  useEffect(() => {
    const marketingBrand = resolveClientMarketingBrand(searchParams);
    persistMarketingBrand(marketingBrand);
    const root = document.documentElement;
    if (marketingBrand === "siteglue") {
      root.setAttribute("data-marketing-brand", "siteglue");
    }
    return () => {
      // Keep attribute if user navigates within SiteGlue session on app host
    };
  }, [searchParams]);

  useLayoutEffect(() => {
    const isNewBuild = searchParams.get("new") === "1";
    const hireIntentId = searchParams.get("hireIntentId")?.trim();

    // After ?new=1 is stripped, allow the next "+ Build new agent" click to reset again.
    if (!isNewBuild) {
      newBuildHandledRef.current = false;
      newBuildUiResetRef.current = false;
      return;
    }
    if (hireIntentId || newBuildUiResetRef.current) return;
    newBuildUiResetRef.current = true;

    // Session was cleared during render; finish UI reset + strip ?new=1.
    bootstrapStartedRef.current = false;
    setWebsiteSuggestion(null);
    setDisplayCompanyName("");
    setLiveAgentName("");
    setRecommendationsSelection(defaultCustomHireSelection());
    // Keep bootstrap complete for a bare fresh build — Trish collects the website URL.
    // Setting false here raced with remounts and left the chat stuck on "Thinking…".
    setBootstrapComplete(true);
    setBootstrapError(null);
    setBuildSessionKey((key) => key + 1);

    // Sync Next.js searchParams (history.replaceState alone leaves ?new=1 in useSearchParams).
    // Also drop sticky website/plan so Start Over cannot re-bootstrap the prior org.
    const params = new URLSearchParams(searchParams.toString());
    params.delete("new");
    params.delete("website");
    params.delete("plan");
    params.delete("hireIntentId");
    params.delete("payment");
    const qs = params.toString();
    router.replace(qs ? `${window.location.pathname}?${qs}` : window.location.pathname, {
      scroll: false,
    });
  }, [searchParams, agentName, router]);

  useEffect(() => {
    if (!showPlanStep) return;

    const resumePendingWebsiteBootstrap = (): boolean => {
      if (bootstrapComplete || isBootstrapping) return false;

      const savedOnboarding = readSavedHireBuilderOnboardingState();
      const pendingUrl = savedOnboarding?.submittedWebsite?.trim();
      const pendingAnalyze =
        savedOnboarding?.step === "analyzing" || Boolean(pendingUrl);
      if (!pendingAnalyze || !pendingUrl) return false;

      const stored = readWebsiteSuggestion();
      if (stored && hireWebsiteSessionMatches(stored.websiteUrl, pendingUrl)) {
        bootstrapStartedRef.current = true;
        setWebsiteSuggestion(stored);
        setRecommendationsSelection(
          readHireRecommendationsSelection() ?? selectionFromWebsiteSuggestion(stored),
        );
        setBootstrapComplete(true);
        return true;
      }

      if (bootstrapStartedRef.current) return false;
      void runWebsiteBootstrap(pendingUrl);
      return true;
    };

    if (resumePendingWebsiteBootstrap()) return;

    if (bootstrapStartedRef.current) return;

    const hireIntentId = searchParams.get("hireIntentId")?.trim();
    const paymentReturn = searchParams.get("payment")?.trim();
    const websiteParam = searchParams.get("website")?.trim();
    const stored = readWebsiteSuggestion();

    if (hireIntentId && paymentReturn === "success") {
      bootstrapStartedRef.current = true;
      if (stored) {
        setWebsiteSuggestion(stored);
        setRecommendationsSelection(
          readHireRecommendationsSelection() ?? selectionFromWebsiteSuggestion(stored),
        );
        setBootstrapComplete(true);
      } else if (websiteParam) {
        void runWebsiteBootstrap(websiteParam);
      } else if (authLoaded && isSignedIn) {
        void utils.project.getMarketplaceHireResumeContext
          .fetch({ hireIntentId })
          .then((ctx) => {
            if (ctx.valid && ctx.websiteUrl) {
              void runWebsiteBootstrap(ctx.websiteUrl);
            } else {
              setBootstrapComplete(true);
            }
          })
          .catch(() => {
            setBootstrapComplete(true);
          });
      } else {
        setBootstrapComplete(true);
      }
      return;
    }

    if (hireIntentId) {
      if (!authLoaded) return;
      if (!isSignedIn) return;
      bootstrapStartedRef.current = true;
      void utils.project.getMarketplaceHireResumeContext
        .fetch({ hireIntentId })
        .then((ctx) => {
          if (ctx.action === "complete") {
            router.replace(buildMarketplaceHireCompletionPath(hireIntentId));
            return;
          }
          if (!ctx.valid) {
            bootstrapStartedRef.current = false;
            setNeedsWebsiteInput(true);
            return;
          }
          const resumeWebsite = ctx.websiteUrl.trim();
          if (stored && hireWebsiteSessionMatches(stored.websiteUrl, resumeWebsite)) {
            setWebsiteSuggestion(stored);
            setRecommendationsSelection(
              readHireRecommendationsSelection() ?? selectionFromWebsiteSuggestion(stored),
            );
            setBootstrapComplete(true);
            return;
          }
          void runWebsiteBootstrap(resumeWebsite);
        })
        .catch(() => {
          bootstrapStartedRef.current = false;
          setNeedsWebsiteInput(true);
        });
      return;
    }

    if (websiteParam) {
      bootstrapStartedRef.current = true;
      if (stored && hireWebsiteSessionMatches(stored.websiteUrl, websiteParam)) {
        setWebsiteSuggestion(stored);
        setRecommendationsSelection(
          readHireRecommendationsSelection() ?? selectionFromWebsiteSuggestion(stored),
        );
        setBootstrapComplete(true);
        return;
      }
      void runWebsiteBootstrap(websiteParam);
      return;
    }

    // Google OAuth return lands on bare /agents/hire/custom?google_oauth=… — restore
    // the website suggestion so Trish/chat stay enabled without a full re-scrape.
    const googleOauth = searchParams.get("google_oauth")?.trim();
    if (googleOauth && stored) {
      bootstrapStartedRef.current = true;
      setWebsiteSuggestion(stored);
      setRecommendationsSelection(
        readHireRecommendationsSelection() ?? selectionFromWebsiteSuggestion(stored),
      );
      setBootstrapComplete(true);
      return;
    }
    if (googleOauth && readHireCustomPreviewState()) {
      bootstrapStartedRef.current = true;
      setBootstrapComplete(true);
      return;
    }

    // Do not hydrate stale egpt-hire-website-suggestion on a bare /agents/hire/custom
    // visit — that resurrects "Onboard Christie" (or similar) from a prior session.
    // Resume only via ?website=, ?hireIntentId=, or pending Build-tab analyze above.
    bootstrapStartedRef.current = true;
    setBootstrapComplete(true);
  }, [
    showPlanStep,
    bootstrapComplete,
    isBootstrapping,
    searchParams,
    runWebsiteBootstrap,
    authLoaded,
    isSignedIn,
    router,
    utils.project.getMarketplaceHireResumeContext,
  ]);

  useEffect(() => {
    const stored = readHirePlanSelection();
    if (stored?.planId === defaultPlanId) {
      setPlanSelection(stored);
    }
  }, [defaultPlanId]);

  const useSplitLayout = showPlanStep;
  const recommendationsMode = Boolean(websiteSuggestion && recommendationsSelection);

  const handleSubmitWebsite = useCallback(
    (url: string) => {
      void runWebsiteBootstrap(url);
    },
    [runWebsiteBootstrap],
  );

  const allAgentsQuery = api.project.getStaffingAgents.useQuery(undefined, {
    enabled: recommendationsMode,
  });
  const allAgents = (allAgentsQuery.data?.agents ?? []) as MoonlightAgentListItem[];

  const suggestedAgents = useMemo(() => {
    if (!websiteSuggestion) return [];
    return rankSuggestedAgents(
      allAgents,
      websiteSuggestion.categoryId,
      `${websiteSuggestion.businessName} ${websiteSuggestion.categoryLabel} ${websiteSuggestion.summary}`,
      3,
    );
  }, [allAgents, websiteSuggestion]);

  const [hireFormStep, setHireFormStep] = useState<1 | 2 | 3 | 4>(1);

  const handleStepChange = useCallback(
    (step: 1 | 2 | 3 | 4) => {
      setHireFormStep(step);
      if (!useSplitLayout && step !== 1) {
        setDemoSidebarMode("closed");
      }
    },
    [useSplitLayout],
  );

  const handleTestAgent = useCallback((projectId: string) => {
    setDemoTab("aiChat");
    setDemoAgentId(projectId);
    setDemoSidebarMode("expanded");
  }, []);

  const siteGluePlanId = useMemo((): AiEmployeePlanId => {
    return aiEmployeePlanIdFromHirePlanId(planSelection.planId);
  }, [planSelection.planId]);

  const planTotalCents = useMemo(() => {
    if (isSiteGlueBrand) {
      return aiEmployeePlanPriceCents(siteGluePlanId);
    }
    if (recommendationsMode && recommendationsSelection) {
      return computeRecommendationsTotalCents(recommendationsSelection);
    }
    return computeHireTotalCents(planSelection);
  }, [
    isSiteGlueBrand,
    siteGluePlanId,
    recommendationsMode,
    recommendationsSelection,
    planSelection,
  ]);

  const planTotalLabel = useMemo(
    () =>
      isSiteGlueBrand
        ? formatAiEmployeePlanHeaderLabel(siteGluePlanId)
        : formatHireTotalPrice(planTotalCents),
    [isSiteGlueBrand, siteGluePlanId, planTotalCents],
  );

  const handleLiveDemo = useCallback((agent: MoonlightAgentListItem) => {
    setDemoTab(preferredDemoTab(agent));
    setDemoAgentId(agent.id);
    setDemoSidebarMode("expanded");
  }, []);

  const handleCloneAgent = useCallback((agent: MoonlightAgentListItem) => {
    setPendingCloneAgent(agent);
  }, []);

  // SiteGlue hire: no Victoria concierge FAB — Book a Demo / Contact Sales are header text links.
  const sidebarProjectId = isSiteGlueBrand ? null : demoAgentId;
  const demoSidebarOpen =
    !isSiteGlueBrand &&
    demoSidebarMode !== "closed" &&
    !!sidebarProjectId &&
    !useSplitLayout &&
    hireFormStep === 1;

  const headerLinkClass =
    "text-sm font-medium text-muted-foreground transition-colors hover:text-foreground";

  const headerCompanyName =
    displayCompanyName.trim() ||
    resolveHireBusinessDisplayName(
      websiteSuggestion?.businessName,
      websiteSuggestion?.websiteUrl,
    ) ||
    "";

  const displayAgentName =
    liveAgentName.trim() || agentName.trim() || "";

  /** Company context from website/clone — subtitle can personalize before agent name is chosen. */
  const showPersonalizedOnboardTitle = Boolean(
    headerCompanyName &&
      (websiteSuggestion || pendingCloneAgent || displayCompanyName.trim()),
  );

  /** AI Phone System landing → Trish onboarding (?campaign=ai-phone-system). */
  const isAiPhoneSystemCampaign =
    searchParams.get("campaign") === "ai-phone-system" ||
    searchParams.get("referrer") === "ai-phone-system";

  /** Only show "Onboard Denise" after the user picks/confirms an agent name in Build. */
  const onboardTitleName = (() => {
    const name = liveAgentName.trim();
    if (!name || /^(onboard|hire|signup|sign|your agent)$/i.test(name)) {
      return "your agent";
    }
    return name;
  })();

  const onboardPageTitle = isAiPhoneSystemCampaign
    ? "Build your online phone system in less than 2 minutes!"
    : isSiteGlueBrand
      ? showPersonalizedOnboardTitle
        ? `Onboard ${onboardTitleName}`
        : "Onboard Your AI Employee in Minutes"
      : showPersonalizedOnboardTitle
        ? `Onboard ${onboardTitleName}`
        : `Build your ${productLabel} Agent`;

  const onboardPageSubtitle = isAiPhoneSystemCampaign
    ? headerCompanyName
      ? `Chat with Trish to build an AI Phone System for ${headerCompanyName} that can answer questions, book appointments, take messages, and connect customers to your team.`
      : "Chat with Trish to build an AI Phone System that can answer questions, book appointments, take messages, and connect customers to your team."
    : showPersonalizedOnboardTitle
      ? isSiteGlueBrand
        ? `SiteGlue AI Employee for ${headerCompanyName}`
        : `${productLabel} Agent for ${headerCompanyName}`
      : isSiteGlueBrand
        ? "Meet Trish, your AI Solutions Consultant. She’ll analyze your organization, recommend the right AI Employee, and help you get started for free."
        : "Answer a few quick questions — we'll analyze your site and load your preview.";

  const showHireConfigurator = !showPlanStep || authLoaded;
  const dashboardMode = Boolean(isSignedIn && useSplitLayout && !hideSiteGlueChrome);

  const handleOnboardingGoalsComplete = useCallback(
    (goals: string[]) => {
      setRecommendationsSelection((current) => {
        const base =
          current ??
          (websiteSuggestion
            ? selectionFromWebsiteSuggestion(websiteSuggestion)
            : defaultCustomHireSelection());
        const updated = applyOnboardingGoalsToSelection(base, goals);
        writeHireRecommendationsSelection(updated);
        return updated;
      });
    },
    [websiteSuggestion],
  );

  /** Always normalize so partial patches (e.g. Trish scheduling) cannot drop locationDetails. */
  const handleRecommendationsChange = useCallback((next: HireRecommendationsSelection) => {
    const normalized = normalizeRecommendationsSelection(next);
    writeHireRecommendationsSelection(normalized);
    setRecommendationsSelection(normalized);
  }, []);

  return (
    <div
      className={cn(
        "flex h-dvh max-h-dvh min-h-0 w-full min-w-0 flex-col overflow-hidden bg-white",
      )}
    >
      {isSiteGlueBrand && !hideSiteGlueChrome ? (
        <div className="shrink-0">
          <MarketingNav brandId="siteglue" hideAuthCtas />
        </div>
      ) : null}
      {dashboardMode ? (
        <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/projects">{brand.terminology.myAgentsHeading}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {isSiteGlueBrand ? "Onboard Your AI Employee" : "Build new agent"}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      ) : null}
      <div className="flex min-h-0 w-full min-w-0 flex-1">
        <div
          className="flex min-h-0 min-w-0 w-full flex-1 flex-col transition-[margin-right] duration-300"
          style={demoSidebarOpen ? { marginRight: `${demoSidebarWidthPx}px` } : undefined}
        >
          <div
            className={cn(
              "flex shrink-0 gap-3 border-b border-gray-200 bg-white px-3 py-2.5 sm:px-4",
              useSplitLayout
                ? "flex-col sm:flex-row sm:items-center sm:justify-between"
                : "items-center",
            )}
          >
            <div className="min-w-0 flex-1">
              {!dashboardMode && !isSignedIn && !useSplitLayout ? (
                <Link
                  href={backHref}
                  className="mb-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {backLabel}
                </Link>
              ) : !dashboardMode && useSplitLayout && !isSiteGlueBrand ? (
                <span className="mb-1 inline-flex items-center gap-1 px-1 text-xs text-gray-400">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Configure your agent, then test and deploy
                </span>
              ) : !dashboardMode && !useSplitLayout ? (
                <span className="mr-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-400">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Complete onboarding to deploy
                </span>
              ) : null}
              {useSplitLayout ? (
                <div>
                  <h1 className="text-left text-base font-semibold leading-tight text-slate-900 sm:text-xl lg:text-2xl">
                    {onboardPageTitle}
                  </h1>
                  {isSiteGlueBrand && !showPersonalizedOnboardTitle && !isAiPhoneSystemCampaign ? (
                    <p className="mt-0.5 text-xs font-semibold text-slate-700 sm:hidden">
                      {planTotalLabel}
                    </p>
                  ) : null}
                  <p
                    className={cn(
                      "mt-1 text-sm text-muted-foreground",
                      isAiPhoneSystemCampaign || showPersonalizedOnboardTitle
                        ? "hidden sm:block"
                        : "hidden lg:block",
                    )}
                  >
                    {onboardPageSubtitle}
                  </p>
                </div>
              ) : null}
            </div>
            {useSplitLayout ? (
              <div className="hidden shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-1 self-end sm:flex sm:self-auto">
                {isSiteGlueBrand ? (
                  <>
                    <button
                      type="button"
                      className={headerLinkClass}
                      onClick={() => {
                        trackSiteGlueCta({
                          action: "bookDemo",
                          label: SITEGLUE_CTA_CONFIG.bookDemo.label,
                          location: "header",
                          destination: "contact-sales-modal",
                        });
                        setContactSalesOpen(true);
                      }}
                    >
                      Book a Demo
                    </button>
                    <button
                      type="button"
                      className={headerLinkClass}
                      onClick={() => {
                        trackSiteGlueCta({
                          action: "contactSales",
                          label: SITEGLUE_CTA_CONFIG.contactSales.label,
                          location: "header",
                          destination: "contact-sales-modal",
                        });
                        setContactSalesOpen(true);
                      }}
                    >
                      Contact Sales
                    </button>
                    <span className="text-sm font-semibold text-slate-900">{planTotalLabel}</span>
                  </>
                ) : (
                  <p className="text-right text-xl font-semibold text-slate-900 sm:text-2xl">
                    {planTotalLabel}
                  </p>
                )}
              </div>
            ) : null}
          </div>

          <div
            className={cn(
              "min-h-0 w-full min-w-0 flex-1",
              useSplitLayout
                ? "flex flex-col overflow-hidden bg-white p-0"
                : "overflow-y-auto bg-gray-50 p-4 sm:p-6 lg:p-8",
            )}
          >
            <div
              className={cn(
                useSplitLayout
                  ? "flex min-h-0 w-full min-w-0 flex-1 flex-col"
                  : cn(
                      "mx-auto rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-8",
                      recommendationsMode ? "max-w-6xl" : "max-w-2xl",
                    ),
              )}
            >
              {showHireConfigurator ? (
              <HireAgentForm
                key={buildSessionKey}
                templateProjectId={templateProjectId}
                agentName={agentName}
                showPlanStep={showPlanStep}
                splitLayout={useSplitLayout}
                wizardMode={showPlanStep}
                bootstrapComplete={bootstrapComplete}
                autoGeneratePreview={showPlanStep}
                onPreviewGenerateStart={() => {}}
                onPreviewGenerateComplete={() => {}}
                onPreviewGenerateError={() => {}}
                planSelection={planSelection}
                onPlanSelectionChange={setPlanSelection}
                formTitle={
                  useSplitLayout
                    ? undefined
                    : recommendationsMode && websiteSuggestion
                      ? isSiteGlueBrand
                        ? `SiteGlue AI Employee for ${websiteSuggestion.businessName}`
                        : `${productLabel} Agent Onboarding for ${websiteSuggestion.businessName}`
                      : pageTitle
                }
                templateDefaults={templateDefaults}
                recommendationsMode={recommendationsMode}
                recommendationsSelection={recommendationsSelection ?? undefined}
                onRecommendationsChange={setRecommendationsSelection}
                recommendationsBusinessName={websiteSuggestion?.businessName}
                websiteSuggestion={websiteSuggestion}
                suggestedAgents={suggestedAgents}
                suggestedAgentsLoading={allAgentsQuery.isLoading}
                onLiveDemo={handleLiveDemo}
                onCloneAgent={handleCloneAgent}
                pendingCloneAgent={pendingCloneAgent}
                onPendingCloneHandled={() => setPendingCloneAgent(null)}
                onStepChange={handleStepChange}
                onTestAgent={handleTestAgent}
                onDisplayCompanyNameChange={setDisplayCompanyName}
                onDisplayAgentNameChange={setLiveAgentName}
                onSubmitWebsite={handleSubmitWebsite}
                isBootstrapping={isBootstrapping}
                bootstrapError={bootstrapError}
                onOnboardingGoalsComplete={handleOnboardingGoalsComplete}
                onStartOver={() => {
                  // Clear scrape/bootstrap state without remounting the form (keeps Trish chat alive).
                  bootstrapStartedRef.current = false;
                  setWebsiteSuggestion(null);
                  setDisplayCompanyName("");
                  setLiveAgentName("");
                  setBootstrapComplete(false);
                  setBootstrapError(null);
                  setIsBootstrapping(false);
                  setRecommendationsSelection(defaultCustomHireSelection());
                }}
                partnerConfig={partnerConfig}
              />
              ) : (
                <div className="flex min-h-[320px] items-center justify-center text-sm text-slate-500">
                  {isBootstrapping ? "Analyzing your website…" : "Loading…"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isSiteGlueBrand && sidebarProjectId && !useSplitLayout && hireFormStep === 1 ? (
        <LandingAgentSidebar
          key={sidebarProjectId}
          projectId={sidebarProjectId}
          layout="agent-profile"
          mode={demoSidebarMode}
          onModeChange={setDemoSidebarMode}
          sidebarWidthPx={demoSidebarWidthPx}
          onSidebarWidthChange={setDemoSidebarWidthPx}
          defaultWidgetTab={demoTab}
        />
      ) : null}

      {isSiteGlueBrand ? (
        <ContactSalesModal open={contactSalesOpen} onOpenChange={setContactSalesOpen} />
      ) : null}
    </div>
  );
}
